import { useEffect, useState } from 'react';
import { Dialog, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { registrarControlExpediente, confirmarNovedadExpediente } from '../../services/tramiteService';
import { apellidoNombre } from '../../utils/nombrePersona';
import { ESTADOS_ANSES, accionesDeConfirmacion } from '../../pages/Listados/paraAprobar';

const COLOR = '#1e40af';

/**
 * Las dos acciones de una fila de "Para aprobar":
 *  - modo 'control': alguien miró el expediente en Mi ANSES y anota cómo está. También deja
 *    cargar el N° de expediente y la fecha de ingreso cuando el trámite no los tiene.
 *  - modo 'confirmar': hay una novedad (Favorable / Desfavorable / Citado) y un usuario decide
 *    cómo sigue (en pantalla: "Pasar a Favorables", "Decidir cómo sigue", "Cargar citación"). Favorable pasa el trámite a Favorables con la fecha de resolución de ANSES.
 *
 * Los datos se reinician al abrir (dependen de `open` y de la fila, no de objetos nuevos en cada
 * render: ver la regla de modales del proyecto).
 */
export default function ModalExpedienteParaAprobar({ open, modo, fila, onClose, onGuardado }) {
    const [resultado, setResultado] = useState('EN_CURSO');
    const [etapa, setEtapa] = useState('');
    const [fechaResolucion, setFechaResolucion] = useState('');
    const [observacion, setObservacion] = useState('');
    const [nroExpediente, setNroExpediente] = useState('');
    const [fechaIngresoAnses, setFechaIngresoAnses] = useState('');
    const [accion, setAccion] = useState('');
    const [guardando, setGuardando] = useState(false);

    const idTramite = fila?.idTramite;
    useEffect(() => {
        if (!open || !fila) return;
        const actual = fila.estadoAnses || 'EN_CURSO';
        setResultado(actual);
        setEtapa(modo === 'confirmar' ? (fila.etapaAnses || '') : '');
        setFechaResolucion(fila.fechaResolucionAnses || '');
        setObservacion('');
        setNroExpediente(fila.nroExpediente || '');
        setFechaIngresoAnses(fila.fechaIngresoAnses || '');
        setAccion(accionesDeConfirmacion(actual)[0]?.valor || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, idTramite, modo]);

    if (!fila) return null;
    const confirmando = modo === 'confirmar';
    const acciones = accionesDeConfirmacion(resultado);
    const pideFecha = resultado === 'FAVORABLE' || resultado === 'DESFAVORABLE';

    const guardar = async () => {
        setGuardando(true);
        try {
            const base = { resultado, etapa: etapa || null, fechaResolucion: fechaResolucion || null, observacion: observacion || null };
            if (confirmando) {
                await confirmarNovedadExpediente(idTramite, { ...base, accion });
                toast.success(accion === 'PASAR_A_FAVORABLE' ? 'Pasó a Favorables' : 'Decisión registrada');
            } else {
                await registrarControlExpediente(idTramite, {
                    ...base,
                    nroExpediente: nroExpediente || null,
                    fechaIngresoAnses: fechaIngresoAnses || null,
                });
                toast.success('Control registrado');
            }
            onGuardado?.({ modo, accion });
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'No se pudo guardar');
        } finally {
            setGuardando(false);
        }
    };

    // Encabezado y botones fijos; solo el cuerpo scrollea. Con overflow:hidden en el papel, en
    // pantallas bajas o con zoom el botón de confirmar quedaba fuera de la vista sin scroll.
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}>
            <Box sx={{ flexShrink: 0, background: `${COLOR}12`, borderBottom: `1px solid ${COLOR}33`, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="fw-bold" style={{ color: '#1e293b', fontSize: '1.05rem' }}>
                    <i className={`bi ${confirmando ? 'bi-bell' : 'bi-search'} me-2`} style={{ color: COLOR }}></i>
                    {confirmando ? `ANSES informó: ${ESTADOS_ANSES[resultado]?.etiqueta || 'novedad'} — ¿cómo sigue?` : 'Registrar control del expediente'}
                </span>
                <IconButton onClick={onClose} size="small" aria-label="Cerrar"><CloseIcon fontSize="small" /></IconButton>
            </Box>

            <Box sx={{ p: 3, overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                <div className="mb-3 pb-3 border-bottom">
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>
                        {apellidoNombre(fila.apellidoCliente, fila.nombreCliente) || 'Sin nombre'}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        {fila.tipoTramite || 'Trámite'}{fila.nroExpediente && ` · Exp. ${fila.nroExpediente}`}
                    </div>
                </div>

                <label className="form-label fw-semibold" style={{ fontSize: '0.9rem' }}>
                    {confirmando ? 'Resultado' : '¿Cómo está en ANSES?'}
                </label>
                <div className="d-flex flex-wrap gap-2 mb-3" role="radiogroup" aria-label="Resultado">
                    {Object.entries(ESTADOS_ANSES).filter(([valor, e]) => !e.soloRobot || resultado === valor).map(([valor, e]) => {
                        const activo = resultado === valor;
                        return (
                            <button
                                key={valor}
                                type="button"
                                role="radio"
                                aria-checked={activo}
                                disabled={confirmando}
                                onClick={() => { setResultado(valor); setAccion(accionesDeConfirmacion(valor)[0]?.valor || ''); }}
                                style={{
                                    border: `2px solid ${activo ? e.fg : e.borde}`, background: activo ? e.bg : '#fff',
                                    color: e.fg, borderRadius: 20, padding: '4px 14px', fontWeight: activo ? 700 : 500,
                                    fontSize: '0.85rem', opacity: confirmando && !activo ? 0.4 : 1,
                                }}
                            >
                                {e.etiqueta}
                            </button>
                        );
                    })}
                </div>

                <div className="mb-3">
                    <label className="form-label" htmlFor="etapaAnses" style={{ fontSize: '0.85rem' }}>Etapa o detalle en ANSES <span className="text-muted">(opcional)</span></label>
                    <input id="etapaAnses" className="form-control form-control-sm" value={etapa} maxLength={255}
                        placeholder="Ej.: Proceso de evaluación, Análisis y posterior liquidación…"
                        onChange={(e) => setEtapa(e.target.value)} />
                </div>

                {pideFecha && (
                    <div className="mb-3">
                        <label className="form-label" htmlFor="fechaResolucion" style={{ fontSize: '0.85rem' }}>Fecha de resolución de ANSES</label>
                        <input id="fechaResolucion" type="date" className="form-control form-control-sm" style={{ maxWidth: 200 }}
                            value={fechaResolucion} onChange={(e) => setFechaResolucion(e.target.value)} />
                        {resultado === 'FAVORABLE' && (
                            <div className="form-text">Es la fecha favorable que queda en el cobro.</div>
                        )}
                    </div>
                )}

                {confirmando ? (
                    <div className="mb-3">
                        <label className="form-label fw-semibold" style={{ fontSize: '0.9rem' }}>¿Qué se hace?</label>
                        {acciones.map((a) => (
                            <div className="form-check" key={a.valor}>
                                <input className="form-check-input" type="radio" name="accion" id={`accion-${a.valor}`}
                                    checked={accion === a.valor} onChange={() => setAccion(a.valor)} />
                                <label className="form-check-label" htmlFor={`accion-${a.valor}`} style={{ fontSize: '0.88rem' }}>{a.etiqueta}</label>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="row g-2 mb-3">
                        <div className="col-sm-7">
                            <label className="form-label" htmlFor="nroExpediente" style={{ fontSize: '0.85rem' }}>N° de expediente</label>
                            <input id="nroExpediente" className="form-control form-control-sm" value={nroExpediente} maxLength={60}
                                placeholder="024-27-12345678-9-066-01" onChange={(e) => setNroExpediente(e.target.value)} />
                        </div>
                        <div className="col-sm-5">
                            <label className="form-label" htmlFor="fechaIngresoAnses" style={{ fontSize: '0.85rem' }}>Ingreso a ANSES</label>
                            <input id="fechaIngresoAnses" type="date" className="form-control form-control-sm"
                                value={fechaIngresoAnses} onChange={(e) => setFechaIngresoAnses(e.target.value)} />
                        </div>
                    </div>
                )}

                <div className="mb-1">
                    <label className="form-label" htmlFor="observacionControl" style={{ fontSize: '0.85rem' }}>Observación <span className="text-muted">(opcional)</span></label>
                    <textarea id="observacionControl" className="form-control form-control-sm" rows={2} value={observacion}
                        onChange={(e) => setObservacion(e.target.value)} />
                </div>
            </Box>

            <Box sx={{ flexShrink: 0, px: 3, py: 2, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <button type="button" className="btn btn-light" onClick={onClose} disabled={guardando}>Cancelar</button>
                <button type="button" className="btn btn-primary" onClick={guardar} disabled={guardando || (confirmando && !accion)}>
                    {guardando ? 'Guardando…' : confirmando ? 'Guardar decisión' : 'Registrar control'}
                </button>
            </Box>
        </Dialog>
    );
}
